import { useMatch } from 'react-router-dom'
import useActiveInstallationContext from 'src/Libs/installations/hooks/use-active-installation-context'
import { useInstallationAccessType } from 'src/Libs/installations/hooks/use-installation-access-type'
import { useFetchChargingSessionsTotalElements } from '../../charging-sessions/hooks/use-fetch-charging-sessions-total-elements'
import { useFetchWallboxes } from '../../wallboxes/api/use-fetch-wallboxes'
import { EMobilityImplementation, useSelectEMobilityImplementation } from 'src/Libs/e-mobility/use-select-e-mobility-implementation'

interface UseShouldEnableEMobilitySectionHook {
  shouldEnableEMobilityLink: boolean
  shouldEnableEMobilityRoutes: boolean
}

/**
 * Hook used to decide if e-mobility sidebar item should be enabled or not.
 *
 * At the moment, it should only be visible if:
 *
 * 1. there are wallboxes currently connected
 * to the installation specified by the installationId argument
 *
 * OR if
 *
 * 2. there was at least one session started in this installation. From that point on,
 * even if all wallboxes are disconnected from the installation, the e-mobility section
 * will stay enabled so historical charging data can be accessed
 *
 * Important: since information for 1 and 2 must be fetched from two separate endpoints,
 * there is some loading time that must be taken into account when deciding if the link should
 * be enabled or not. For better ux, we do not disable the link immediately to avoid flickering. Also,
 * since e-mobility routes will be mounted conditionally, we must be 100% certain that all loading
 * has completed before deciding to unmount them. Otherwise, entering an e-mobility url in the address
 * bar directly will cause the router to initially throw a "404 - Page not found" and cancel any further
 * fetching.
 *
 * Additionally, these two endpoints can potentially respond with an error, but
 * this is not necessarily a reason to disable the whole e-mobility section. For example, the endpoint
 * for fetching wallboxes might be broken at the moment, but charging sessions, dashboard, tariffs and charging
 * card tabs can still be accessed properly. Since this ux eases development and is a rare case, we still enable
 * the e-mobility section when such error occurs. During this time, we keep the e-mobility Routes mounted so that
 * the user has a chance to be shown an error message inside the e-mobility section.
 */
export const useShouldEnableEMobilitySection = (): UseShouldEnableEMobilitySectionHook => {
  const {
    installationId: activeInstallationId,
    installation,
    storageId,
    storageDeviceLink,
    deviceLinksLoading,
    installationError,
  } = useActiveInstallationContext()
  const installationAccessType = useInstallationAccessType(installation)
  const skipCheck = Boolean(installation?.decommissionedAt)
  const storageLinkDisabled = !storageId || (!deviceLinksLoading && (!storageDeviceLink || !!storageDeviceLink.decommissionedAt))
  const canAccessEMobilityLink = !storageLinkDisabled && installationAccessType === 'full-access' && !installationError

  /**
   * The id of the relevant installation can come from two places:
   *
   * 1. redux store, if installation is marked as "active", but
   * not necessarily shown in the url. This happens when an installer user is in the installation overview/table
   *
   * 2. url, if user has entered some e-mobility url directly in the browser's address bar
   *
   * For this reason, we must check both places when deciding what installation to target by id
   */

  const match = useMatch('/e-mobility/:installationId/*')
  const isAttemptingToOpenEMobilityViaURL = !!match
  const urlInstallationId = match?.params.installationId
  const urlInstallationIdNumber = urlInstallationId ? Number(urlInstallationId) : undefined
  const installationId = urlInstallationIdNumber || activeInstallationId /** Order is important! URL should have precedence */
  const { eMobilityImplementation, isLoading: isEMobilityImplementationLoading } = useSelectEMobilityImplementation({
    installationId,
    skipCheck: skipCheck || !installationId,
  })
  const shouldSkipEMobilityDataFetch = skipCheck || !installationId || eMobilityImplementation !== EMobilityImplementation.V2

  const {
    hadChargedAtLeastOnceInThePast,
    isLoading: isChargingSessionsLoading,
    isError,
  } = useFetchChargingSessionsTotalElements({
    installationId,
    isInstallationDeleted: shouldSkipEMobilityDataFetch,
  })

  const { wallboxes, isLoading: isWallboxesLoading } = useFetchWallboxes(installationId, shouldSkipEMobilityDataFetch)

  if (skipCheck || !installationId) {
    /**
     * If installation is not selected(which sets the installation id to undefined),
     * e-mobility section should not be enabled, nor should any Routes be mounted.
     */
    return { shouldEnableEMobilityLink: false, shouldEnableEMobilityRoutes: false }
  }

  /**
   * Once new e-mobility frontend is rolled out to all customers, remove this condition.
   */
  if (!isEMobilityImplementationLoading && eMobilityImplementation === EMobilityImplementation.V1) {
    return {
      shouldEnableEMobilityLink: canAccessEMobilityLink,
      shouldEnableEMobilityRoutes: true,
    }
  }

  const atLeastOneWallboxConnected = wallboxes ? wallboxes.length > 0 : false
  const hasDataToShow = atLeastOneWallboxConnected || (hadChargedAtLeastOnceInThePast && !isError)
  const shouldEnableEMobilityLink = canAccessEMobilityLink && hasDataToShow
  const shouldEnableEMobilityRoutes =
    hasDataToShow ||
    (isAttemptingToOpenEMobilityViaURL &&
      (isEMobilityImplementationLoading ||
        isWallboxesLoading ||
        isChargingSessionsLoading)) /* mount Routes until loading finishes to prevent 404 errors */

  return { shouldEnableEMobilityLink, shouldEnableEMobilityRoutes }
}
